import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { multiLanguageAlphaNumericRegexHelper } from 'remarketing-angular-library';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, pluck, startWith, tap } from 'rxjs/operators';
import { SaComponent } from 'smartauction-frontend-micro-library';

@Component({
    selector: 'app-edit-comment',
    templateUrl: './edit-comment.component.html',
    styleUrls: ['./edit-comment.component.scss'],
    imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslocoDirective]
})
export class EditCommentComponent extends SaComponent implements AfterViewInit, OnInit {
  private _comment: string;
  form: UntypedFormGroup;
  commentLength = 0;
  commentToolong = false;
  @ViewChild('commentBoxInput') commentBoxInput: ElementRef<HTMLInputElement>;
  @Input() index: number;
  @Input() set comment(comment: string) {
    this._comment = comment;
    this.commentLength = this.comment ? this.comment.length : 0;
    this.form = this.formBuilder.group({
      comment: [this.comment ? this.comment : '', [Validators.maxLength(150)]]
    });
    this.commentLength$ = this.form.get('comment').valueChanges.pipe(
      map(val => val.length),
      distinctUntilChanged(),
      tap(len => this.notifyCommentLength(len)),
      startWith(this.comment ? this.comment.length : 0)
    );
  }

  @Output() cancelEvent = new EventEmitter<string>();
  @Output() updateEvent = new EventEmitter<string>();

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  get comment() {
    return this._comment;
  }

  get commentBoxId() {
    return `comments_edit_textbox_${this.index}`;
  }

  constructor(private formBuilder: UntypedFormBuilder) {
    super();
  }

  ngOnInit() {
    this.getTranslations();
  }

  replaceInvalidCharacters(e) {
    e.target.value = multiLanguageAlphaNumericRegexHelper(e.target.value);
  }

  private messages$$ = new Subject<{ type: 'polite' | 'assertive' | 'clear'; message: string }>();

  commentLength$: Observable<number>;

  politeMessage$ = this.messages$$.asObservable().pipe(
    startWith({ type: 'polite', message: '' }),
    filter(({ type }) => type === 'polite' || type === 'clear'),
    pluck('message')
  );

  assertiveMessage$ = this.messages$$.asObservable().pipe(
    startWith({ type: 'assertive', message: '' }),
    filter(({ type }) => type === 'assertive' || type === 'clear'),
    pluck('message')
  );

  ngAfterViewInit() {
    this.commentBoxInput?.nativeElement.focus();
  }

  commentKeydown(e: KeyboardEvent) {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      this.messages$$.next({ type: 'clear', message: '' });
    }
    if (this.commentLength === 150) {
      this.messages$$.next({ type: 'clear', message: '' });
      setTimeout(() => {
        this.messages$$.next({ type: 'assertive', message: 'Max characters reached.' });
      }, 50);
    }
  }

  updateComment() {
    const userComment = this.form.controls['comment'].value.trim();
    this.updateEvent.emit(userComment);
  }

  cancel() {
    this.cancelEvent.emit();
  }

  private notifyCommentLength(length: number) {
    const eighty = Math.ceil(150 * 0.8);
    const ninety = Math.ceil(150 * 0.9);
    const ninetyFive = Math.ceil(150 * 0.95);
    const oneHundred = 150;
    const message = (used: number) => `${150 - used} out of 150 characters remaining`;
    switch (length) {
    case eighty:
      this.messages$$.next({ type: 'polite', message: message(eighty) });
      break;
    case ninety:
      this.messages$$.next({ type: 'polite', message: message(ninety) });
      break;
    case ninetyFive:
      this.messages$$.next({ type: 'assertive', message: message(ninetyFive) });
      break;
    case oneHundred:
      this.messages$$.next({ type: 'assertive', message: message(oneHundred) });
      break;
    default:
      break;
    }
  }
}
